import React, { useState } from "react";

import { admin_cse } from "@/config/roles";
import { Button2 } from "@/ui2/atoms/Button";
import EditIcon from "@/assets/images/icons2/icon--edit.svg?component";
import { isAuthorized } from "@/redux/actions/auth";
import { Text } from "@/component-library/Text";
import { TextColors, TextTypes } from "@/component-library/constants";

import { CareTeamModalityModal } from "../../CareTeam/components/CareTeamModalityModal";
import { MODAL_MODES } from "../constants";
import { useModalityText } from "../../../api/queries/useModalityText";

import { EmptyGoalsDisplay } from "./EmptyGoalsDisplay";
import { NonEmptyGoalsDisplay } from "./NonEmptyGoalsDisplay";
export function CareGoalRoleEntry(props) {
  const label = props.label;
  const providerType = props?.providerType;
  const patientId = props?.patientId;
  const providerTypeCurrentGoalsAndObjectives = props?.providerTypeCurrentGoalsAndObjectives || [];
  const careTeamMembersForRole = props.careTeamMembersForRole;

  const isProviderOnCareTeam = props.isProviderOnCareTeam;
  const isActiveProviderType = providerType && providerType === props.role;
  const canEdit = isAuthorized(admin_cse)
    ? props.profile?.is_edit_care_plan_enabled
    : isActiveProviderType && isProviderOnCareTeam;
  const shouldShowAddGoal = canEdit && providerTypeCurrentGoalsAndObjectives.length < 3;

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState(MODAL_MODES.EMPTY);
  const [selectedCareTeamMember, setSelectedCareTeamMember] = useState(null);

  const { data: modalityOptions, isLoading } = useModalityText({ patientId });

  const onCloseModal = () => {
    setShowModal(false);
    setSelectedCareTeamMember(null);
    setModalMode(MODAL_MODES.EMPTY);
  };

  const onModalSubmission = async () => {
    await props.refreshCareTeam();
    onCloseModal();
  };

  const onSelectModalityDetails = (careTeamMember) => {
    setModalMode(MODAL_MODES.ADD);
    setSelectedCareTeamMember(careTeamMember);
    setShowModal(true);
  };

  const onUpdateModalityDetails = (careTeamMember) => {
    setModalMode(MODAL_MODES.UPDATE);
    setSelectedCareTeamMember(careTeamMember);
    setShowModal(true);
  };

  if (isLoading) return null;

  const careTeamMemberModalityFrequencyExists = (member, canEditModality, index) => {
    return (
      <div
        className="CareGoalsCard--role_entry_modality"
        key={`${member.username}-${index}-frequency-exists`}
      >
        {careTeamMembersForRole.length > 1 && (
          <Text className="text-break" type={TextTypes.light}>
            {`${member.name} － `}
          </Text>
        )}

        <Text className="text-break" type={TextTypes.light}>
          {member?.modality_other || member.modality}
        </Text>

        <Text type={TextTypes.light}> • </Text>
        <Text className="d-inline" type={TextTypes.light}>
          {member.frequency}
        </Text>

        {member?.default_appointment_type && (
          <>
            <Text type={TextTypes.light}> • </Text>
            <Text className="d-inline" type={TextTypes.light}>
              {member.default_appointment_time} Min
            </Text>
          </>
        )}

        {canEditModality && (
          <>
            <Text type={TextTypes.light}> • </Text>
            <button
              className="CarePlanIconButton--icon_button CareTeamMemberCard--edit_modality_details d-inline"
              onClick={() => onUpdateModalityDetails(member)}
            >
              <p className="apti-LinkV2">Edit</p>
            </button>
          </>
        )}
      </div>
    );
  };

  const careTeamMemberModalityFrequencyNotExists = (member, canEditModality, index) => {
    const isOnlyOneModalityOption =
      modalityOptions.modality && modalityOptions.modality[member?.type].length === 1;

    let careTeamMemberModalityInfo = "";

    if (careTeamMembersForRole.length > 1) {
      careTeamMemberModalityInfo += member.name;
    }

    if (careTeamMemberModalityInfo && isOnlyOneModalityOption) {
      careTeamMemberModalityInfo += ` － ${modalityOptions.modality[member.type][0]}`;
    } else if (!careTeamMemberModalityInfo && isOnlyOneModalityOption) {
      careTeamMemberModalityInfo += modalityOptions.modality[member.type][0];
    }

    if (careTeamMemberModalityInfo && canEditModality) {
      careTeamMemberModalityInfo += " • ";
    }

    return (
      <div
        className="CareGoalsCard--role_entry_modality d-flex"
        key={`${member.username}-${index}-no-frequency`}
      >
        <Text className="text-break" type={TextTypes.light}>
          {careTeamMemberModalityInfo}
        </Text>

        {canEditModality && modalityOptions.modality && (
          <Button2
            icon={<EditIcon />}
            onPress={() => onSelectModalityDetails(member)}
            size="xs"
            variant="link"
          >
            {isOnlyOneModalityOption ? "Add Frequency" : "Add Modality & Frequency"}
          </Button2>
        )}
      </div>
    );
  };

  return (
    <div className="CareGoalsCard--role_entry">
      <div className="CareGoalsCard--role_entry_header">
        <div className="d-flex">
          <Text color={TextColors.purple} type={TextTypes.label}>
            {label}
          </Text>
        </div>

        <div>
          {careTeamMembersForRole.map((member, index) => {
            const canEditModality = isAuthorized(admin_cse)
              ? Boolean(props?.profile?.is_edit_care_plan_enabled)
              : member?.provider_id === props?.profile?.username;

            if (!member?.modality) {
              return careTeamMemberModalityFrequencyNotExists(member, canEditModality, index);
            }

            return careTeamMemberModalityFrequencyExists(member, canEditModality, index);
          })}
        </div>

        {showModal && (
          <CareTeamModalityModal
            careTeamMember={selectedCareTeamMember}
            mode={modalMode}
            onClose={onCloseModal}
            onModalSubmission={onModalSubmission}
            options={modalityOptions}
            patientId={patientId}
          />
        )}
      </div>

      {!providerTypeCurrentGoalsAndObjectives.length ? (
        <EmptyGoalsDisplay
          onAddGoalClick={props.onAddGoalClick}
          shouldShowAddGoal={shouldShowAddGoal}
        />
      ) : (
        <NonEmptyGoalsDisplay
          canEdit={canEdit}
          currentGoalsAndObjectives={providerTypeCurrentGoalsAndObjectives}
          onAddGoalClick={props.onAddGoalClick}
          onAddObjectiveClick={props.onAddObjectiveClick}
          onCompleteObjectiveClick={props.onCompleteObjectiveClick}
          onEditObjectiveClick={props.onEditObjectiveClick}
          onRemoveObjectiveClick={props.onRemoveObjectiveClick}
          shouldShowAddGoal={shouldShowAddGoal}
        />
      )}
    </div>
  );
}
